import type { UserRole } from './lib/roles';
export type { UserRole };
export type { CRMUser } from './lib/db';

export type LeadStatus =
  | 'Lead'
  | 'Quoted'
  | 'Approved'
  | 'Finalizing'
  | 'Complete'
  | 'Transferred'
  | 'Declined'
  | 'Not Awarded'
  | '30-Day Unresponsive';

export type PipelineBoardCategory =
  | 'Opportunity'
  | 'Won'
  | 'Unqualified'
  | 'Lose';

export type PipelineType =
  | 'Opportunity'
  | 'Won'
  | 'Unqualified'
  | 'Lose'
  | (string & {});

export const PIPELINE_BOARD_CATEGORIES: PipelineBoardCategory[] = [
  'Opportunity',
  'Won',
  'Unqualified',
  'Lose',
];

export const STATUS_DISPLAY_LABELS: LeadStatus[] = [
  'Lead',
  'Quoted',
  'Approved',
  'Finalizing',
  'Complete',
  'Transferred',
  'Declined',
  'Not Awarded',
  '30-Day Unresponsive',
];

export const DEFAULT_STATUS_COLORS: Record<string, string> = {
  'Lead': '#0086f0',
  'Quoted': '#4ade80',
  'Approved': '#fdab3d',
  'Finalizing': '#1e40af',
  'Complete': '#00c875',
  'Transferred': '#a25ddc',
  'Declined': '#64748b',
  'Not Awarded': '#e2445c',
  '30-Day Unresponsive': '#7c3aed',
};

export const LEGACY_STATUS_TO_CANONICAL: Record<string, LeadStatus> = {
  'New': 'Lead',
  'New Lead': 'Lead',
  'Website Lead': 'Lead',
  'Attempted to Contact': 'Lead',
  'Cold Call Lead': 'Lead',
  'Sent Quote': 'Quoted',
  'Sent Credit App': 'Quoted',
  'Awarded': 'Approved',
  'Accepted': 'Approved',
  'Customer Setup': 'Approved',
  'In Process': 'Approved',
  'Working on it': 'Approved',
  'Awaiting First Shipment': 'Approved',
  'Won': 'Approved',
  'Onboarding': 'Finalizing',
  'Broker': 'Transferred',
  'Brokers': 'Transferred',
  'Shipper': 'Transferred',
  'Lost': 'Declined',
  'Revisit Later | ADD FU DATE': 'Declined',
};

const LEGACY_FACTORY_DEFAULTS: Record<string, string[]> = {
  'Quoted': ['#579bfc'],
  'Awarded': ['#22c55e'],
  'Accepted': ['#22c55e'],
  'Onboarding': ['#fdab3d'],
  'Cold Call Lead': ['#ff5c93', '#579bfc'],
  'Revisit Later | ADD FU DATE': ['#64748b', '#fdab3d'],
  'Won': ['#00c875'],
  'Working on it': ['#fdab3d'],
  'In Process': ['#fdab3d'],
  'Lost': ['#e2445c'],
  'Website Lead': ['#0086f0', '#a25ddc'],
  'New Lead': ['#00a896', '#0086f0', '#c4c4c4'],
  'New': ['#00a896', '#0086f0', '#c4c4c4'],
  'Attempted to Contact': ['#a25ddc', '#ffcb00'],
  'Sent Quote': ['#579bfc'],
  'Sent Credit App': ['#22c55e', '#a25ddc'],
  'Broker': ['#6366f1', '#ff158a'],
  'Brokers': ['#6366f1', '#ff158a'],
  'Shipper': ['#f43f5e', '#a25ddc'],
  'Customer Setup': ['#fdab3d'],
  'Awaiting First Shipment': ['#00c875'],
};

export const OBSOLETE_LEGACY_STATUS_KEYS = new Set<string>(Object.keys(LEGACY_STATUS_TO_CANONICAL));

function normalizeHexColor(color: string | undefined | null, fallback = '#64748b'): string {
  if (!color || typeof color !== 'string') return fallback;
  const trimmed = color.trim();
  if (!trimmed) return fallback;
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

export function migrateStatusColors(saved?: Record<string, string> | null): Record<string, string> {
  const result: Record<string, string> = { ...DEFAULT_STATUS_COLORS };
  if (!saved || typeof saved !== 'object') {
    return result;
  }

  const savedEntries = Object.entries(saved).filter(
    ([k, v]) => typeof k === 'string' && k.trim().length > 0 && typeof v === 'string' && v.trim().length > 0
  );

  for (const canonicalKey of Object.keys(DEFAULT_STATUS_COLORS)) {
    const exact = saved[canonicalKey];
    if (typeof exact === 'string' && exact.trim()) {
      const normalizedExact = normalizeHexColor(exact, DEFAULT_STATUS_COLORS[canonicalKey]);
      const oldDefaultsForCanonical = (LEGACY_FACTORY_DEFAULTS[canonicalKey] || []).map((d) => d.toLowerCase());
      if (!oldDefaultsForCanonical.includes(normalizedExact.toLowerCase())) {
        result[canonicalKey] = normalizedExact;
      }
      continue;
    }
    const ciMatch = savedEntries.find(([k]) => k.toLowerCase() === canonicalKey.toLowerCase());
    if (ciMatch) {
      const normalizedCi = normalizeHexColor(ciMatch[1], DEFAULT_STATUS_COLORS[canonicalKey]);
      const oldDefaultsForCanonical = (LEGACY_FACTORY_DEFAULTS[canonicalKey] || []).map((d) => d.toLowerCase());
      if (!oldDefaultsForCanonical.includes(normalizedCi.toLowerCase())) {
        result[canonicalKey] = normalizedCi;
      }
      continue;
    }
    const customizedLegacy = savedEntries.find(([k, v]) => {
      if (LEGACY_STATUS_TO_CANONICAL[k] !== canonicalKey) return false;
      const normalizedVal = normalizeHexColor(v).toLowerCase();
      const oldDefaults = (LEGACY_FACTORY_DEFAULTS[k] || []).map((d) => d.toLowerCase());
      return oldDefaults.length > 0 && !oldDefaults.includes(normalizedVal);
    });
    if (customizedLegacy) {
      result[canonicalKey] = normalizeHexColor(customizedLegacy[1], DEFAULT_STATUS_COLORS[canonicalKey]);
    }
  }

  const canonicalLowerSet = new Set(Object.keys(DEFAULT_STATUS_COLORS).map((k) => k.toLowerCase()));
  const obsoleteLowerSet = new Set(Array.from(OBSOLETE_LEGACY_STATUS_KEYS).map((k) => k.toLowerCase()));

  for (const [k, v] of savedEntries) {
    const trimmedKey = k.trim();
    const lowerKey = trimmedKey.toLowerCase();
    if (canonicalLowerSet.has(lowerKey) || obsoleteLowerSet.has(lowerKey)) {
      continue;
    }
    result[trimmedKey] = normalizeHexColor(v);
  }

  return result;
}

export function resolveStatusColor(
  status: string | undefined | null,
  statusColors?: Record<string, string> | null
): string {
  const raw = (status || 'Lead').trim();
  const map = statusColors && Object.keys(statusColors).length > 0 ? statusColors : DEFAULT_STATUS_COLORS;

  if (map[raw]) {
    return normalizeHexColor(map[raw]);
  }

  const lower = raw.toLowerCase();
  for (const [key, val] of Object.entries(map)) {
    if (key.toLowerCase() === lower && val) {
      return normalizeHexColor(val);
    }
  }

  const canonical =
    LEGACY_STATUS_TO_CANONICAL[raw] ||
    Object.entries(LEGACY_STATUS_TO_CANONICAL).find(([k]) => k.toLowerCase() === lower)?.[1];

  if (canonical) {
    if (map[canonical]) {
      return normalizeHexColor(map[canonical]);
    }
    for (const [key, val] of Object.entries(map)) {
      if (key.toLowerCase() === canonical.toLowerCase() && val) {
        return normalizeHexColor(val);
      }
    }
    if (DEFAULT_STATUS_COLORS[canonical]) {
      return DEFAULT_STATUS_COLORS[canonical];
    }
  }

  if (DEFAULT_STATUS_COLORS[raw]) {
    return DEFAULT_STATUS_COLORS[raw];
  }
  for (const [key, val] of Object.entries(DEFAULT_STATUS_COLORS)) {
    if (key.toLowerCase() === lower) {
      return val;
    }
  }

  return '#64748b';
}

export type SalespersonName = 'Danean Allan' | 'Sarah Nesbitt' | 'Fernando Perez' | 'Meybeth Rodriguez';
export const AUTHORIZED_SALESPERSONS: SalespersonName[] = [
  'Danean Allan',
  'Sarah Nesbitt',
  'Fernando Perez',
  'Meybeth Rodriguez',
];

export type ServiceType = 'LTL' | 'Air Freight' | 'Truckload' | 'Consolidation' | 'US Customs Brokerage';
export const SERVICE_TYPES: ServiceType[] = [
  'LTL',
  'Air Freight',
  'Truckload',
  'Consolidation',
  'US Customs Brokerage',
];

export type IndustryType = 'Floral' | 'Mass Market' | 'Wholesale' | 'Retail' | 'Non-Floral' | 'Plants' | 'Produce';
export const INDUSTRIES: IndustryType[] = [
  'Floral',
  'Mass Market',
  'Wholesale',
  'Retail',
  'Non-Floral',
  'Plants',
  'Produce',
];

export type OutboundState = 'FL' | 'CA';
export const OUTBOUND_STATES: OutboundState[] = ['FL', 'CA'];

export type OutboundWarehouse = 'MIA' | 'PLM' | 'OXN' | 'ATL' | 'CIN' | 'PIE' | 'VIS';
export const OUTBOUND_WAREHOUSES: OutboundWarehouse[] = ['MIA', 'PLM', 'OXN', 'ATL', 'CIN', 'PIE', 'VIS'];

export type DayOfService = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
export const DAYS_OF_SERVICE: DayOfService[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export type LeadType = 'Cold Call' | 'Network Event' | 'Inbound Inquiry';
export const LEAD_TYPES: LeadType[] = [
  'Cold Call',
  'Network Event',
  'Inbound Inquiry',
];

export type UnitOfMeasure = 'Cube' | 'Pallet' | 'Box' | 'Carton' | 'Shipment' | 'Truckload' | 'Flat Rate' | 'Other';
export const UNITS_OF_MEASURE: UnitOfMeasure[] = ['Cube', 'Pallet', 'Box', 'Carton', 'Shipment', 'Truckload', 'Flat Rate', 'Other'];

export type QuoteFrequency =
  | '3x-Weekly'
  | '2x-Weekly'
  | 'Weekly'
  | 'Monthly'
  | 'Once'
  | 'Seasonal'
  | 'TBD';

export const QUOTE_FREQUENCIES: QuoteFrequency[] = [
  '3x-Weekly',
  '2x-Weekly',
  'Weekly',
  'Monthly',
  'Once',
  'Seasonal',
  'TBD',
];

export type PaymentTermsOption = 'Net 7 w/ Deposit' | 'Net 7' | 'Net 14' | 'Net 30';
export const PAYMENT_TERMS_OPTIONS: PaymentTermsOption[] = [
  'Net 7 w/ Deposit',
  'Net 7',
  'Net 14',
  'Net 30',
];

export type LaneApprovalStatus = 'pending' | 'sent' | 'approved';

export interface LeadLane {
  id?: number | string;
  leadId?: string;
  laneNumber: number;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  sameAsConsignee?: boolean;
  shipperName?: string;
  consigneeName?: string;
  rateAgreementRole?: 'Bill To/Consignee' | 'Bill To/Shipper' | 'Bill To';
  consigneeCity?: string;
  consigneeState?: string;
  consigneeZip?: string;
  serviceTypes?: ServiceType[];
  industries?: IndustryType[];
  outboundStates?: OutboundState[];
  outboundWarehouses?: OutboundWarehouse[];
  daysOfService?: DayOfService[];
  forecastVolume?: number;
  dollarAmount?: number;
  uom?: UnitOfMeasure;
  serviceFrequency?: QuoteFrequency;
  handlingFee?: number;
  noHandlingFee?: boolean;
  fuelSurchargePercent?: number;
  noFuelSurcharge?: boolean;
  minimumShipmentAmount?: number;
  paymentTerms?: string;
  depositAmount?: number | null;
  estimatedDealValue?: number;
  fullDeliveryView?: string;
  fullQuoteView?: string;
  approvalStatus?: LaneApprovalStatus;
  sentToOpsAt?: string;
  sentToOpsBy?: string;
  approvedAt?: string;
  approvedBy?: string;
  createdAt?: string;
  createdBy?: string;
}

export interface ActivityLogEntry {
  id: string;
  type?: 'status_change' | 'note_added' | 'email_sent' | 'file_attached' | 'pipeline_moved' | 'details_updated' | string;
  action?: string;
  timestamp: string;
  details: string;
  user: string;
}

export interface Lead {
  id: string;
  name: string;
  company: string;
  email: string;
  dealValue: number;
  pipeline: PipelineType;
  status: string; // Dynamic status based on Excel/Monday data
  lastContacted: string;
  owner?: SalespersonName | string;
  contactPerson?: string;
  careOf?: string;
  location?: string;
  accountNumber?: string;
  billToAccountNumber?: string;
  leadNotes?: string;
  nextSteps?: string;
  phone?: string;
  service?: string;
  industry?: string;
  appLink?: string;
  quoteDetails?: string;
  activityLog?: ActivityLogEntry[];
  followUpDate?: string; // Date string for follow-up (e.g. YYYY-MM-DD)
  followUpCompletedAt?: string; // ISO timestamp when follow-up was marked done; empty/undefined = not done
  followUpCompleted?: boolean; // Boolean indicating if the current follow-up task is finished
  followUpNotes?: string; // Captures immediate next steps or notes for upcoming outreach
  quickNotes?: string; // Informal rich text or raw text meeting notes per lead
  isArchived?: boolean; // Archived lead status

  // Operational & Address Fields
  billToType?: 'Bill To/Consignee' | 'Bill To/Shipper' | 'Bill To';
  consigneeName?: string;
  consigneeAccount?: string;
  consigneeCity?: string;
  consigneeState?: string;
  consigneeZip?: string;
  shipperName?: string;
  shipperAccount?: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  fullDeliveryView?: string;
  routingOpsApproved?: boolean;

  // Multi-select Selections
  leadType?: LeadType[];
  serviceTypes?: ServiceType[];
  industries?: IndustryType[];
  outboundStates?: OutboundState[];
  outboundWarehouses?: OutboundWarehouse[];
  daysOfService?: DayOfService[];

  // Quoting Engine Fields
  quoteDollarAmount?: number;
  uom?: UnitOfMeasure;
  handlingFeeAmount?: number;
  noHandlingFee?: boolean;
  fuelSurcharge?: boolean;
  fuelSurchargePercent?: number;
  noFuelSurcharge?: boolean;
  forecastVolume?: number;
  quoteFrequency?: QuoteFrequency;
  minimumMonthlyAmount?: number;
  minimumShipmentAmount?: number;
  paymentTerms?: string;
  depositAmount?: number | null;
  fullQuoteView?: string;
  estimatedDealValue?: number;

  // Multi-lane Operational & Quoting Lanes
  lanes?: LeadLane[];

  // Quote Request & Operations Approval Audit Trail
  quoteRequestedBy?: string;
  quoteRequestedAt?: string;
  routingApprovedBy?: string;
  routingApprovedAt?: string;

  // Milestone Dates
  inquiryDate?: string;
  proposalDate?: string;
  acceptedDate?: string;
  accountSetupDate?: string;
  firstShipmentDate?: string;
  finalFollowUpDate?: string; // Date string when final 1-month follow-up was completed (YYYY-MM-DD)
  monthlyRevenue?: number; // Actual 1st-month revenue calculated from A4 invoices

  // Date-Stamped Onboarding Checkboxes (Informational)
  creditAppSentAt?: string;
  creditAppSentBy?: string;
  rateAgreementSentAt?: string;
  rateAgreementSentBy?: string;
  internalEmailsSentAt?: string;
  internalEmailsSentBy?: string;
  firstDeliveryFollowUpAt?: string;
  firstDeliveryFollowUpBy?: string;

  // Off-Ramp Disqualifiers
  isTransferred?: boolean;
  transferredReason?: 'Armellini Air Express' | 'JA Flowers' | 'Armellini Freight Brokerage' | 'C/O AEL Account' | string;
  isDeclined?: boolean;
  declinedReason?: 'Delivery Environment' | 'Routing' | 'Temperature' | 'Commodity' | 'Credit' | string;
  isNotAwarded?: boolean;
  notAwardedReason?: 'Rate' | 'DOS' | string;
  is30DayUnresponsive?: boolean;
}


export interface KPIStats {
  totalLeads: number;
  conversionRate: number;
  pipelineValue: number;
}

// --- NON-FLORAL LTL LOGISTICS MODULE TYPES ---

export interface Shipper {
  a4shipid?: string; // Primary Key
  shipperName: string;
  shippername?: string;
  shipper_account?: string;
  shipperAccount?: string;
  shipper_code?: string;
  handling_id?: string; // (Handler handling Link)
  email?: string;
  shipperEmail?: string;
  status?: number | boolean;
  state?: string;
  shipperState?: string;
  abbrv?: string;
  porequired?: number | boolean;
  onhold?: number | boolean;
  warehouseid?: string;
  recvwarehouse?: string;
  address?: string;
  shipperAddress?: string;
  phone?: string;
  shipperPhone?: string;
  city?: string;
  shipperCity?: string;
  zipcode?: string;
  ShipperCutOff?: string;
  parentid?: string;
}

export interface Consignee {
  consigneeAccount: string; // Primary Key
  consigneeName: string;
  consigneeAddress?: string;
  receivingWindow?: string;
  consigneeAddress2?: string;
  consigneeCity?: string;
  consigneeState?: string;
  consigneeZip?: string;
  consigneeEmail?: string;
  consigneePhone?: string;
  consigneeFax?: string;
}

export interface BillTo {
  billToAccount: string; // Primary Key
  billToName: string;
  billToAddress?: string;
  billToAddress2?: string;
  billToCity?: string;
  billToState?: string;
  billToZip?: string;
  billToEmail?: string;
  signatureCell?: string;
  signatureEmail?: string;
  billToPhone?: string;
  billToFax?: string;
}

export interface Commodity {
  commodity: string; // Primary Key
}

export interface LaraEmail {
  id: string; // Primary Key
  pickUpDate?: string;
  palletTenderedBy?: string;
  shipperName?: string;
  consigneeName?: string;
  palletCount?: number;
  commodity?: string;
  emailSent?: string; // Date string or null
}

export interface NonltlShipment {
  quoteNumber: string; // Primary Key
  produceNonltl: 'PRODUCE' | 'NONLTL';
  timeSigned?: string;
  timeBilled?: string;
  status: 'PENDING SIGNATURE' | 'SIGNED' | 'BILLED';
  pickUpDate?: string;
  outboundDate?: string;
  shipperAccount?: string;
  shipperName?: string;
  consignee?: string;
  consigneeName?: string;
  consigneeAddress?: string;
  poNumber?: string;
  pickupNumber?: string;
  palletCount?: number;
  commodity?: string;
  ratePerPallet?: number;
  billTo?: string;
  billToName?: string;
  palletTenderedBy?: string;
  emailToLara?: string;
  apptReq?: 'APPT REQ' | 'FCFS';
  receivingWindow?: string;
  billToEmail?: string;
  signatureCell?: string;
  signatureEmail?: string;
  dateEntered?: string;
  formatedMobile?: string;
  tenderedCarrier?: string;
  arriveAtCustomerDate?: string;
  pickUpTime?: string;
  notes?: string;
  salesperson?: string;
}

// --- ARMELLINI AELIS 4 LOGISTICS API TYPES ---

export interface AelisTokenStatus {
  active: boolean;
  expiresInSeconds?: number;
  tokenType?: string;
  audience?: string;
  error?: string;
}

export interface AelisDoor {
  doorId?: string;
  doorNumber?: string;
  warehouseCode?: string;
  status?: string;
  description?: string;
  [key: string]: any;
}

export interface AelisWarehouseCode {
  code?: string;
  name?: string;
  location?: string;
  description?: string;
  [key: string]: any;
}

export interface AelisLayout {
  layoutId?: string;
  warehouseCode?: string;
  name?: string;
  zone?: string;
  [key: string]: any;
}

export interface AelisCutoff {
  cutoffId?: string;
  warehouseCode?: string;
  cutoffTime?: string;
  destinationRegion?: string;
  [key: string]: any;
}

export interface AelisScannerUser {
  userId?: string;
  username?: string;
  warehouseCode?: string;
  role?: string;
  [key: string]: any;
}

export interface AelisAccountShipper {
  shipperId?: string;
  accountNumber?: string;
  name?: string;
  city?: string;
  state?: string;
  address?: string;
  [key: string]: any;
}

export interface AelisAccountConsignee {
  consigneeId?: string;
  accountNumber?: string;
  name?: string;
  city?: string;
  state?: string;
  address?: string;
  [key: string]: any;
}

export interface AelisDayOfService {
  accountNumber?: string;
  serviceDays?: string[];
  origin?: string;
  destination?: string;
  [key: string]: any;
}

export interface AelisRegion {
  regionId?: string;
  code?: string;
  name?: string;
  state?: string;
  [key: string]: any;
}

export interface AelisHolidayPeriod {
  holidayId?: string;
  name?: string;
  startDate?: string;
  endDate?: string;
  [key: string]: any;
}

export interface AelisBlockedAccount {
  accountNumber?: string;
  accountName?: string;
  reason?: string;
  blockedDate?: string;
  [key: string]: any;
}

export interface AelisCoolerInventoryItem {
  unitId?: string;
  warehouseCode?: string;
  location?: string;
  temperature?: number;
  receivedDate?: string;
  consigneeName?: string;
  shipperName?: string;
  packageType?: string;
  quantity?: number;
  status?: string;
  [key: string]: any;
}

export interface AelisDockReceivePayload {
  unitId: string;
  warehouseCode: string;
  doorNumber?: string;
  shipperAccount?: string;
  consigneeAccount?: string;
  pieceCount?: number;
  tempReading?: number;
  receivedBy?: string;
  notes?: string;
  [key: string]: any;
}

export interface AelisShipmentOrder {
  shipmentId?: string;
  orderNumber?: string;
  shipperAccount?: string;
  consigneeAccount?: string;
  numberUnits?: number;
  numberUnitsReceived?: number;
  status?: string;
  dateCreated?: string;
  [key: string]: any;
}

export interface AelisUnitReceivedStatus {
  unitId: string;
  received: boolean;
  receivedAt?: string;
  location?: string;
  warehouseCode?: string;
  shipmentId?: string;
  pieceCount?: number;
  tempReading?: number;
  [key: string]: any;
}

export interface AelisProduct {
  productId?: string;
  code?: string;
  description?: string;
  category?: string;
  [key: string]: any;
}

// --- CUSTOMER MASTER & ACCOUNT DETAIL TYPES ---

export interface CustomerMasterAccount {
  accountNumber: string;
  accountName: string;
  accountType: 'Consignee' | 'Shipper' | 'Bill To' | 'Master Account';
  status: 'Active' | 'Inactive' | 'On Hold' | 'Pending';
  statusReason?: string;
  parentAccount?: string;
  parentAccountNumber?: string;
  subParentAccount?: string;
  subParentNumber?: string;
  isSubAccount: boolean;
  billingName: string;
  nickname?: string;
  email: string;
  phone: string;
  isBillTo: boolean;
  paymentTerms: string;
  billToAccount?: string;
  billToAccountNumber?: string;
  features: {
    accessibleDock: boolean;
    bolCcn: boolean;
    emailInvoices: boolean;
    floral: boolean;
    floralChains: boolean;
    atlProduce?: boolean;
    podRequired?: boolean;
    palletReceipts?: boolean;
    lumperStop?: boolean;
    enforceDeliveryWindow?: boolean;
  };
  address: {
    billingAddress: string;
    billingCity: string;
    billingState: string;
    billingZip: string;
    shippingAddress: string;
    shippingCity: string;
    shippingState: string;
    shippingZip: string;
    routingZipcode?: string;
  };
  instructions: {
    alarmInstructions: string;
    deliveryInstructions: string;
  };
  integration: {
    mcLeodId?: string;
  };
  lastEditedBy?: string;
  lastEditedDate?: string;
}

export interface AccountTariff {
  id: string;
  code: string;
  description: string;
  baseClass: string;
  minCharge: number;
  ratePerCwt: number;
  fuelSurchargePct: number;
  effectiveDate: string;
  expirationDate: string;
  status: 'Active' | 'Expired' | 'Draft';
}

export interface AccountMinimum {
  id: string;
  commodityType: string;
  minimumCharge: number;
  weightMinLbs: number;
  cubeMinCuFt: number;
  palletMinCount: number;
  notes?: string;
}

export interface AccountCaseClaim {
  id: string;
  type: 'Case' | 'Claim';
  title: string;
  status: 'Open' | 'Pending Info' | 'Under Review' | 'Resolved' | 'Closed';
  dateOpened: string;
  claimAmount?: number;
  resolutionDate?: string;
  assignedTo?: string;
  description: string;
}

export interface AccountHierarchyNode {
  accountNumber: string;
  accountName: string;
  accountType: string;
  location?: string;
  isCurrent?: boolean;
  children?: AccountHierarchyNode[];
}

export interface FilterRule {
  id: string;
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'does_not_contain' | 'greater_than' | 'less_than' | 'is_empty' | 'is_not_empty' | 'relative_date';
  value: string;
}

export interface SortRule {
  field: string;
  direction: 'asc' | 'desc';
}

export interface SmartView {
  id: string;
  name: string;
  isDefault?: boolean;
  filterRules: FilterRule[];
  sortRules: SortRule[];
  columns?: any[];
  createdAt: string;
}

// --- WHAT'S NEW & ANNOUNCEMENT BANNERS FEATURE SPOTLIGHT TYPES ---

export type BannerTone = "feature" | "info" | "success" | "warning" | "alert";
export type BannerDisplayMode = "top_banner" | "modal_showcase" | "both";

export interface FeatureHighlightItem {
  id: string;
  iconName: string;
  title: string;
  description: string;
  badge?: "NEW" | "IMPROVED" | "PRO TIP" | "BETA" | "IMPORTANT" | string;
  actionUrl?: string;
  actionLabel?: string;
}

export interface AnnouncementBanner {
  id: string;
  title: string;
  message: string;
  tone: BannerTone;
  displayMode: BannerDisplayMode;
  isActive: boolean;
  targetRoles?: (UserRole | string)[];
  targetDepartmentIds?: string[];
  targetDepartmentNames?: string[];
  targetUserEmails?: string[];
  featureItems?: FeatureHighlightItem[];
  ctaLabel?: string;
  ctaAction?: string;
  ctaUrl?: string;
  dismissible: boolean;
  expiresAt?: string;
  createdAt: string;
  createdBy: string;
  createdByName?: string;
  dismissedBy?: string[];
}

/**
 * Evaluates whether a given user should see an announcement banner based on status,
 * role targeting, department targeting, expiration, and user dismissal.
 */
export function shouldUserSeeAnnouncement(
  announcement: AnnouncementBanner,
  user: { uid: string; role?: string; email?: string; departmentId?: string; departmentIds?: string[] } | null,
  dismissedIds: string[] = []
): boolean {
  if (!announcement.isActive) return false;
  
  // Check expiration if specified
  if (announcement.expiresAt) {
    const expiry = new Date(announcement.expiresAt).getTime();
    if (!isNaN(expiry) && Date.now() > expiry) {
      return false;
    }
  }

  // Check user dismissal
  if (announcement.dismissible) {
    if (dismissedIds.includes(announcement.id)) return false;
    if (user && announcement.dismissedBy && announcement.dismissedBy.includes(user.uid)) {
      return false;
    }
  }

  // If no user is logged in, show general announcements
  if (!user) {
    return (!announcement.targetRoles || announcement.targetRoles.length === 0) &&
           (!announcement.targetDepartmentIds || announcement.targetDepartmentIds.length === 0) &&
           (!announcement.targetUserEmails || announcement.targetUserEmails.length === 0);
  }

  // Check specific email targeting if set
  if (announcement.targetUserEmails && announcement.targetUserEmails.length > 0) {
    const userEmail = (user.email || "").toLowerCase().trim();
    const targetEmails = announcement.targetUserEmails.map(e => e.toLowerCase().trim());
    if (!targetEmails.includes(userEmail)) {
      return false;
    }
  }

  // Check role targeting if set
  if (announcement.targetRoles && announcement.targetRoles.length > 0) {
    if (!user.role || !announcement.targetRoles.includes(user.role)) {
      return false;
    }
  }

  // Check department targeting if set
  if (announcement.targetDepartmentIds && announcement.targetDepartmentIds.length > 0) {
    const userDeptIds = user.departmentIds || (user.departmentId ? [user.departmentId] : []);
    const hasMatchingDept = announcement.targetDepartmentIds.some(id => userDeptIds.includes(id));
    if (!hasMatchingDept) {
      return false;
    }
  }

  return true;
}

// --- ONLINE FORMS & DOCUMENT STORAGE TYPES ---

export type OnlineFormType = 'credit_application' | 'w9' | 'credit_card_authorization' | 'rate_agreement_letter';
export type FormInviteStatus = 'sent' | 'opened' | 'completed' | 'revoked' | 'expired';

export interface RateAgreementLetterFields {
  billToType?: 'Bill To/Consignee' | 'Bill To/Shipper' | 'Bill To';
  letterDate?: string;
  billingEmail?: string;
  accountNumber?: string;
  address?: string;
  cityStateZip?: string;
  originCityState?: string;
  destinationCity?: string;
  destinationState?: string;
  destinationZip?: string;
  destinationCityState?: string;
  consigneeName?: string;
  consigneeAccount?: string;
  consigneeCity?: string;
  consigneeState?: string;
  consigneeZip?: string;
  outboundState?: string;
  dos?: string;
  firstShipmentDate?: string;
  minimumShipmentAmount?: string;
  dollarAmount?: string;
  unitOfMeasure?: string;
  handlingFee?: string;
  palletRate?: string;
  cubeRateDetails?: string;
  minimumShipmentDetails?: string;
  paymentTerms?: string;
  paymentTimeframe?: string;
  lateFeePercentage?: string;
  senderEmail?: string;
  senderName?: string;
  proposalDate?: string;
}

export interface FormInvite {
  id: string;
  leadId: string;
  formType: OnlineFormType;
  companyName: string;
  signerName: string;
  customerEmail: string;
  status: FormInviteStatus;
  instructions?: string | null;
  rateAgreementFields?: RateAgreementLetterFields | null;
  createdBy: string;
  createdAt: string;
  openedAt?: string | null;
  openedIp?: string | null;
  openedUserAgent?: string | null;
  completedAt?: string | null;
  docusealSubmissionId?: number | null;
  docusealSigningUrl?: string | null;
  metadata?: Record<string, any> | null;
}

export interface FormSubmissionAuditEntry {
  action: string;
  timestamp: string;
  ip: string;
  userAgent: string;
  details: string;
}

export interface FormSubmission {
  id: string;
  inviteId: string;
  leadId: string;
  formType: OnlineFormType;
  customerName: string;
  customerEmail: string;
  formData: any;
  signedPdfUrl?: string | null;
  auditLog?: FormSubmissionAuditEntry[] | null;
  submittedAt: string;
}

export type LeadDocumentType = 
  | 'signed_agreement' 
  | 'ein_resale_cert' 
  | 'w9_form' 
  | 'credit_card_auth' 
  | 'attachment' 
  | 'Signed Agreement' 
  | 'EIN Resale Cert' 
  | 'W9 Form' 
  | 'Credit Card Auth' 
  | 'Attachment';

export interface LeadDocument {
  id: string;
  leadId: string;
  submissionId?: string | null;
  documentType: LeadDocumentType;
  fileName: string;
  mimeType: string;
  fileSize: number;
  storagePath: string;
  fileData?: string | null;
  uploadedBy: string;
  createdAt: string;
}


